import React, {FC, useCallback} from 'react';
import {
  useRenderingPrioritization,
  useShouldLoadingView,
  useShouldShowDataView,
} from './RenderingPrioritizer';
import {isValidationError} from './throwable';
import {DataFetcherRequest} from './useRequest';

interface DataFetcherProps<Data extends object, ViewProps extends object> {
  request: DataFetcherRequest<Data, unknown, any>;
  Loading?: React.ComponentType;
  Error: React.ComponentType<React.PropsWithChildren<{error: unknown}>> | null;
  // omit ref from viewProps due to Legacy React.forwardRef type issues
  // issue: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/35572
  // its possible to still pass the ref to the view component by using the viewProps as expected
  View?: React.ComponentType<Data & Omit<ViewProps, 'ref'>>;
  viewProps?: React.ComponentProps<React.ComponentType<ViewProps>>;
  /**
   * Allows to define a default state which is passed down while data fetcher is loading.
   * The loading component (e.g. skeleton) is not shown if a default state exists.
   */
  computeDefaultState?: () => Data | undefined;

  /**
   * renderingPriority: the priority in which the component should be rendered.
   * This number is relative to the other components rendered by the data fetcher in the tree.
   * adding renderingPriority here queues the component to be rendered based on that priority when the request resolves.
   * the priority set here is added to RenderingPrioritizerContext to be considered when rendering the components one by one
   */
  renderingPriority?: number;
  /**
   * By default, the idle state is also considered the loading state. In some situations this is not expected, so this flag can turn the default off
   */
  disableLoadingInIdleState?: boolean;
  /**
   * By default, during a refetch, we keep the view component active with stale data.
   * However, in certain situations, this can be problematic, especially when the component becomes hidden after receiving new data,
   * potentially causing page flickering or triggering multiple tracking events.
   * This flag allows us to disable the default behavior in such cases.
   */
  disableViewWhileRefetching?: boolean;
}

const DefaultFallbackComponent = () => null;

export const DataFetcher = <Data extends object, ViewProps extends object>({
  request,
  Loading: LoadingComponent = DefaultFallbackComponent,
  Error: ErrorComponent = DefaultFallbackComponent,
  View: ViewComponent = DefaultFallbackComponent,
  viewProps,
  computeDefaultState,
  renderingPriority,
  disableLoadingInIdleState,
  disableViewWhileRefetching,
}: DataFetcherProps<Data, ViewProps>): ReturnType<FC<Data & ViewProps>> => {
  if (!process.browser) {
    if (request.data) {
      return <ViewComponent {...request.data} {...(viewProps as ViewProps)} />;
    }

    return !disableLoadingInIdleState && request.isIdle ? (
      <LoadingComponent />
    ) : null;
  }

  if (disableViewWhileRefetching && request.isStale && request.isFetching) {
    return null;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/rules-of-hooks
  const StaleViewComponent = useCallback(
    ViewComponent as any,
    [],
  ) as typeof ViewComponent;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useRenderingPrioritization({
    renderingPriority,
    request,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const showViewComponent = useShouldShowDataView({
    renderingPriority,
    request,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const showLoadingComponent = useShouldLoadingView({
    renderingPriority,
    request,
    disableLoadingInIdleState,
  });

  // show error message after retries have finished
  if (request.error && !request.isLoading) {
    const error = isValidationError(request.error)
      ? request.error.data
      : request.error;

    return ErrorComponent ? <ErrorComponent error={error} /> : null;
  }

  if (showViewComponent) {
    return (
      <StaleViewComponent {...request.data!} {...(viewProps as ViewProps)} />
    );
  }

  if (computeDefaultState) {
    const defaultState = computeDefaultState();
    if (defaultState) {
      return (
        <StaleViewComponent {...defaultState} {...(viewProps as ViewProps)} />
      );
    }
  }

  if (LoadingComponent && showLoadingComponent) {
    return <LoadingComponent />;
  }

  return null;
};
